import {clsx} from 'clsx'

import {Link} from '@github-ui/react-router'
import styles from './SidebarListItem.module.css'

export interface SidebarListItemProps {
  href: string
  text: string
  isSelected?: boolean
  trailingAction?: React.ReactNode
  trailingVisual?: React.ReactNode
  /**
   * When true, the trailing visual and trailing action share a single cell and
   * crossfade on hover instead of rendering side by side. Opt in only where the
   * trailing visual would otherwise be displaced by the action appearing on hover.
   */
  crossfadeTrailing?: boolean
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  className?: string
  leadingVisual?: React.ComponentType
  secondaryText?: string | React.ReactNode
  reloadDocument?: boolean
  linkRef?: React.RefObject<HTMLAnchorElement | null>
}

export function SidebarListItem({
  href,
  text,
  isSelected,
  trailingAction,
  trailingVisual,
  crossfadeTrailing = false,
  onClick,
  className,
  leadingVisual: LeadingVisual,
  secondaryText,
  reloadDocument = false,
  linkRef,
}: SidebarListItemProps) {
  const hasSecondaryText = Boolean(secondaryText)
  const isNonInteractive = href === ''
  const shouldCrossfadeTrailing = crossfadeTrailing && Boolean(trailingVisual) && Boolean(trailingAction)

  const content = (
    <div className={styles.content}>
      <div className={styles.topLine}>
        {LeadingVisual && (
          <span className={styles.statusIcon}>
            <LeadingVisual />
          </span>
        )}
        <span>{text}</span>
      </div>
      {secondaryText ? <p className={styles.secondaryText}>{secondaryText}</p> : null}
    </div>
  )

  return (
    <li className={clsx(styles.item, className)} data-selected={isSelected}>
      {isNonInteractive ? (
        <div className={clsx(styles.link, {[styles.linkWithSecondaryText]: hasSecondaryText})}>{content}</div>
      ) : (
        <Link
          ref={linkRef}
          className={clsx(styles.link, {[styles.linkWithSecondaryText]: hasSecondaryText})}
          to={href}
          aria-current={isSelected ? 'page' : undefined}
          onClick={onClick}
          reloadDocument={reloadDocument}
        >
          {content}
        </Link>
      )}
      {shouldCrossfadeTrailing ? (
        <span className={styles.trailingStack} data-testid="sidebar-trailing-stack">
          <span className={clsx(styles.trailingVisual, styles.trailingVisualCrossfade)}>{trailingVisual}</span>
          <span className={clsx(styles.trailingAction, styles.trailingActionCrossfade)}>{trailingAction}</span>
        </span>
      ) : (
        <>
          {trailingVisual ? <span className={styles.trailingVisual}>{trailingVisual}</span> : null}
          {trailingAction ? <span className={styles.trailingAction}>{trailingAction}</span> : null}
        </>
      )}
    </li>
  )
}
